<script setup lang="ts">
import type { UseMetaWatchlist } from '../../composables/useMetaWatchlist'
import type { MetaDraftValue } from '../../models/watchlist.types'
import { META_MAX_CONFIGS } from '../../service/metaWatchlist'

import MetaPageSearch from './MetaPageSearch.vue'
import MetaWatchlistCard from './MetaWatchlistCard.vue'

import { useI18n } from '@/shared/i18n'
import { Alert, Icon } from '@/shared/ui'

defineProps<{ watchlist: UseMetaWatchlist }>()
const { t } = useI18n()

function patchDraft(
  watchlist: UseMetaWatchlist,
  uid: string,
  key: string,
  value: MetaDraftValue,
): void {
  watchlist.patchValue(uid, key, value)
}
</script>

<template>
  <div class="meta-tab grid gap-md font-sans">
    <Alert v-if="watchlist.atCap.value" tone="warning">
      {{ t('workspaceDefinitions.watchlist.meta.error.cap') }}
    </Alert>
    <template v-else>
      <MetaPageSearch
        :model-value="watchlist.query.value"
        :suggestions="watchlist.filteredPages.value"
        :disabled="watchlist.atCap.value"
        :error-key="watchlist.addErrorKey.value"
        @update:model-value="watchlist.setQuery"
        @add="watchlist.addPage"
      />
      <details class="meta-tab__help text-muted text-[0.8125rem]">
        <summary>
          <Icon name="chevron-down" :size="13" />
          {{ t('workspaceDefinitions.watchlist.meta.help.label') }}
        </summary>
        <div
          class="meta-tab__help-body flex gap-sm mt-sm p-md rounded-md bg-primary-subtle text-primary"
        >
          <Icon name="info" :size="18" />
          <div>
            <ol>
              <li>{{ t('workspaceDefinitions.watchlist.meta.help.step1') }}</li>
              <li>{{ t('workspaceDefinitions.watchlist.meta.help.step2') }}</li>
              <li>{{ t('workspaceDefinitions.watchlist.meta.help.step3') }}</li>
              <li>{{ t('workspaceDefinitions.watchlist.meta.help.step4') }}</li>
            </ol>
            <a
              href="https://www.facebook.com/ads/library"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ t('workspaceDefinitions.watchlist.meta.help.open') }}
              <Icon name="external-link" :size="13" />
            </a>
          </div>
        </div>
      </details>
    </template>

    <strong class="meta-tab__heading text-muted text-[0.875rem]">
      {{
        t('workspaceDefinitions.watchlist.meta.tracked', {
          used: watchlist.drafts.value.length,
          max: META_MAX_CONFIGS,
        })
      }}
    </strong>
    <Alert v-if="watchlist.saveErrorKey.value" tone="danger">{{
      t(watchlist.saveErrorKey.value)
    }}</Alert>
    <div v-if="watchlist.drafts.value.length" class="meta-tab__cards grid gap-sm">
      <MetaWatchlistCard
        v-for="draft in watchlist.drafts.value"
        :key="draft.uid"
        :draft="draft"
        :fields="watchlist.filterFields.value"
        :expanded="watchlist.expandedUid.value === draft.uid"
        :errors="watchlist.errors.value[draft.uid] ?? {}"
        :submitted="watchlist.hasSubmitted.value"
        @toggle="watchlist.toggleDraft(draft.uid)"
        @remove="watchlist.removeDraft(draft.uid)"
        @patch="(key, value) => patchDraft(watchlist, draft.uid, key, value)"
        @touch="watchlist.touchField(draft.uid, $event)"
      />
    </div>
    <p v-else class="meta-tab__empty m-0 p-xl text-center text-muted">
      {{ t('workspaceDefinitions.watchlist.meta.empty') }}
    </p>
  </div>
</template>

<style scoped>
.meta-tab .meta-tab__help summary {
  display: flex;
  align-items: center;
  gap: var(--ui-space-xs);
  font-weight: 600;
  cursor: pointer;
}
.meta-tab .meta-tab__help[open] summary :deep(.ui-icon) {
  transform: rotate(180deg);
}
.meta-tab .meta-tab__help-body ol {
  margin-top: 0;
  margin-inline: 0;
  margin-bottom: var(--ui-space-xs);
  padding-inline-start: var(--ui-space-lg);
}
.meta-tab .meta-tab__help-body a {
  display: inline-flex;
  align-items: center;
  gap: var(--ui-space-2xs);
  font-weight: 600;
}
</style>
